import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Spinner } from "@/components/ui/spinner";
import { X } from "lucide-react";
import { useAllEvents } from "@/hooks/useEvents";
import { useSetParentChildren } from "@/hooks/useParentEvents";
import { toast } from "sonner";
import type { EventResponse } from "@/types";

function formatDate(dateStr: string) {
  return new Intl.DateTimeFormat("pl-PL", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(dateStr));
}

interface ManageChildrenDialogProps {
  parentId: string;
  parentTitle: string;
  currentChildren: EventResponse[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ManageChildrenDialog({
  parentId,
  parentTitle,
  currentChildren,
  open,
  onOpenChange,
}: ManageChildrenDialogProps) {
  const [search, setSearch] = useState("");
  const [hidePast, setHidePast] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(currentChildren.map((c) => c.id)),
  );
  const setChildren = useSetParentChildren();

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setSelectedIds(new Set(currentChildren.map((c) => c.id)));
      setSearch("");
      setHidePast(true);
    }
    onOpenChange(next);
  };

  const { data: result, isLoading } = useAllEvents({
    status: "approved",
    search: search || undefined,
    limit: 50,
    hidePast,
  });

  const availableEvents = (result?.data ?? []).filter(
    (e) => !e.isParent && (e.parentId === null || e.parentId === parentId),
  );

  const toggle = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const removeChild = (id: string) => {
    const next = new Set(selectedIds);
    next.delete(id);
    setSelectedIds(next);
  };

  const handleSave = async () => {
    try {
      await setChildren.mutateAsync({
        parentId,
        childEventIds: [...selectedIds],
      });
      toast.success("Program zaktualizowany");
      onOpenChange(false);
    } catch (err) {
      toast.error("Nie udało się zaktualizować programu", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const selectedList = [
    ...currentChildren.filter((c) => selectedIds.has(c.id)),
    ...availableEvents.filter(
      (e) => selectedIds.has(e.id) && !currentChildren.some((c) => c.id === e.id),
    ),
  ];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Program: {parentTitle}</DialogTitle>
        </DialogHeader>

        {selectedList.length > 0 && (
          <div className="space-y-2">
            <Label>Przypisane ({selectedList.length})</Label>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {selectedList.map((event) => (
                <div
                  key={event.id}
                  className="flex items-center justify-between px-3 py-2 bg-muted rounded-md text-sm"
                >
                  <div>
                    <span className="font-medium">{event.title}</span>
                    <span className="text-muted-foreground ml-2">{formatDate(event.date)}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => removeChild(event.id)}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label>Wyszukaj wydarzenia do dodania</Label>
          <div className="flex gap-2">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Szukaj po tytule..."
              className="flex-1"
            />
            <Button
              variant={hidePast ? "outline" : "default"}
              onClick={() => setHidePast((v) => !v)}
            >
              Pokaż archiwalne
            </Button>
          </div>
        </div>

        {isLoading && <Spinner className="mx-auto" />}
        {!isLoading && availableEvents.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">
            Brak dostępnych wydarzeń.
          </p>
        )}
        {!isLoading && availableEvents.length > 0 && (
          <div className="space-y-1 max-h-60 overflow-y-auto">
            {availableEvents.map((event) => (
              <label
                key={event.id}
                className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted cursor-pointer text-sm"
              >
                <Checkbox
                  checked={selectedIds.has(event.id)}
                  onCheckedChange={() => toggle(event.id)}
                />
                <div className="min-w-0 flex-1">
                  <span className="font-medium truncate block">{event.title}</span>
                  <span className="text-muted-foreground text-xs">
                    {formatDate(event.date)} &middot; {event.locationName || "—"}
                  </span>
                </div>
              </label>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button onClick={handleSave} disabled={setChildren.isPending}>
            {setChildren.isPending ? "Zapisywanie..." : "Zapisz program"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
